import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Popconfirm, Space, Statistic, Tag, Typography } from 'antd';
import { useActiveOrg } from '@/features/organizations/active-org-context';
import { formatMoney } from '@/lib/format';
import { salesApi } from '../sales.api';

/**
 * Sales connector: shows real revenue and the signed webhook endpoint external
 * stores POST orders to. Ingested orders feed analytics (ROAS). Admins can view
 * and rotate the signing secret.
 */
export function SalesConnectorCard({
  orgId,
  projectId,
  canEdit,
}: {
  orgId: string;
  projectId: string;
  canEdit: boolean;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { activeOrg } = useActiveOrg();
  const currency = activeOrg?.settings.currency ?? 'USD';

  const overview = useQuery({
    queryKey: ['sales-overview', orgId, projectId],
    queryFn: () => salesApi.overview(orgId, projectId),
  });
  const configKey = ['sales-config', orgId, projectId] as const;
  const config = useQuery({
    queryKey: configKey,
    queryFn: () => salesApi.getConfig(orgId, projectId),
    enabled: canEdit,
  });
  const rotate = useMutation({
    mutationFn: () => salesApi.rotate(orgId, projectId),
    onSuccess: (data) => {
      queryClient.setQueryData(configKey, data);
      message.success('Signing secret rotated');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to rotate'),
  });

  const copy = (label: string, value: string) => {
    void navigator.clipboard?.writeText(value);
    message.success(`${label} copied`);
  };

  return (
    <Card size="small" title="Sales connector (revenue → ROAS)">
      <Space size="large" wrap style={{ marginBottom: canEdit ? 16 : 0 }}>
        <Statistic
          title="Revenue (30d)"
          value={formatMoney(overview.data?.revenue ?? 0, currency)}
          loading={overview.isLoading}
        />
        <Statistic
          title="Orders (30d)"
          value={overview.data?.orderCount ?? 0}
          loading={overview.isLoading}
        />
      </Space>

      {canEdit ? (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            Point your store&apos;s order webhook here. Sign the raw JSON body with HMAC-SHA256
            using the secret and send it as the <code>X-Signature</code> header.
          </Typography.Text>
          <Space wrap>
            <Typography.Text code style={{ wordBreak: 'break-all' }}>
              {config.data?.webhookUrl ?? '…'}
            </Typography.Text>
            {config.data ? (
              <Button size="small" onClick={() => copy('Webhook URL', config.data.webhookUrl)}>
                Copy URL
              </Button>
            ) : null}
          </Space>
          <Space wrap>
            <Tag color="gold">secret</Tag>
            <Typography.Text code>
              {config.data ? `${config.data.secret.slice(0, 12)}…` : '…'}
            </Typography.Text>
            {config.data ? (
              <Button size="small" onClick={() => copy('Secret', config.data.secret)}>
                Copy secret
              </Button>
            ) : null}
            <Popconfirm
              title="Rotate the signing secret? Existing webhooks must be updated."
              onConfirm={() => rotate.mutate()}
            >
              <Button size="small" danger loading={rotate.isPending}>
                Rotate
              </Button>
            </Popconfirm>
          </Space>
        </Space>
      ) : null}
    </Card>
  );
}
